import { formTitles } from './formTitles'

const {
  BENEFIT_AMOUNTS,
  COVERAGE,
  EXPENSE_QUESTION,
  IMPROVE_SEARCH_RESULTS,
  INVOICE,
  OTHER,
  PHARMACY_PLA,
  PROVIDER_PLA,
  SURGERY_SUPPORT,
} = formTitles

/**
 * Returns an array of request type objects that is used to populate the request
 * list in the poptart. When a request type is selected, the object gets saved
 * to state and is used to populate the request form.
 *
 * @param {string} insuranceProduct - The member's insurance product
 * @param {string} requestTypeTitleFilter - A string that filters the request
 *   types on its title field.
 * @returns {array} - An array of request type objects
 */
export const getRequestTypes = ({
  insuranceProduct,
  requestTypeTitleFilter = '',
  flags = {},
}) => {
  const requestTypes = []

  requestTypes.push(
    // the requestTypeId is used to identify the Jira request type
    {
      attachmentSubtext: '',
      requestTypeId: '248',
      title: EXPENSE_QUESTION,
      fields: ['subject', 'member', 'expense', 'message', 'files'],
      requestType: 'General Claims Request',
      messageSubtext: '',
      messageTitle: 'Message',
    },
    // Filter out for EB and ACA
    {
      attachmentSubtext: '',
      requestTypeId: '236',
      title: INVOICE,
      fields: ['member', 'expense', 'message', 'files'],
      requestType: 'Difficulty with Itemized Bill',
      messageSubtext: '',
      messageTitle: 'Message',
    },
    {
      attachmentSubtext: '',
      requestTypeId: '237',
      title: COVERAGE,
      fields: ['member', 'expense', 'message', 'files'],
      requestType: 'Provider Difficulty',
      messageSubtext: '',
      messageTitle: 'Message',
    },
    // Filter out for EB and ACA
    {
      attachmentSubtext: '',
      requestTypeId: '202',
      title: PROVIDER_PLA,
      fields: [
        'member',
        'service',
        'serviceDate',
        'provider',
        'message',
        'files',
      ],
      requestType: 'PLA/Shopping',
      messageSubtext: '',
      messageTitle: 'Message',
    },
    // Filter out for EB and ACA
    {
      attachmentSubtext: '',
      requestTypeId: '247',
      title: PHARMACY_PLA,
      fields: ['member', 'drug', 'message', 'files'],
      requestType: 'Pharmacy PLA',
      messageSubtext: '',
      messageTitle: 'Message',
    },
    // Filter out for EB and ACA
    {
      attachmentSubtext:
        'Screenshots, emails, estimates—any info from your provider helps! Drop them here if you\u2019ve got them.',
      requestTypeId: '284',
      title: BENEFIT_AMOUNTS,
      fields: ['member', 'message', 'files'],
      requestType: 'Verify Benefit Amount',
      messageSubtext: [
        'What services you\u2019re planning to get',
        'Any cost estimates your provider shared',
        'Any procedure codes (CPT codes) they mentioned',
        'When you\u2019re thinking of having this done',
        'Your provider\u2019s details (name, location, contact info)',
      ],
      messageTitle: 'Tell us what you know about your upcoming service(s)',
    },
  )

  if (flags['member-comms-improve-search-results']) {
    requestTypes.push({
      attachmentSubtext: '',
      requestTypeId: '2298',
      title: IMPROVE_SEARCH_RESULTS,
      fields: ['improve_search_results'],
      requestType: 'Improve Search Results',
      showAttachmentsSection: false,
    })
  }
  requestTypes.push(
    // Filter out for EB and ACA.
    // Surgery intake happens in the complex care MFE, so this entry is not
    // offered in the poptart. It stays here so the inbox can still resolve
    // existing surgery requests by their customerTitle.
    {
      attachmentSubtext:
        'Screenshots, emails, estimates—any info from your provider helps! Drop them here if you\u2019ve got them.',
      requestTypeId: '293',
      title: SURGERY_SUPPORT,
      fields: ['member', 'message', 'files'],
      requestType: 'Surgery Support',
      messageTitle: 'Tell us about your upcoming procedure',
    },
    // there are two different product specific 'other' request types in Jira
    {
      attachmentSubtext: '',
      requestTypeId:
        insuranceProduct === 'ASO' || insuranceProduct === 'GFI'
          ? '221'
          : '209',
      title: OTHER,
      fields: ['subject', 'member', 'expense', 'message', 'files'],
      requestType: 'Other',
      messageSubtext: '',
      messageTitle: 'Message',
    },
  )

  const isRestrictedProduct =
    insuranceProduct === 'FIXED_INDEMNITY' || insuranceProduct === 'ACA'

  return requestTypes.filter(({ title }, index) => {
    // Filter by product type (index-based)
    const shouldRemoveByProduct =
      isRestrictedProduct && [2, 4, 5, 6, 7].includes(index)

    // Filter by request type title
    const titleMatchesFilter = requestTypeTitleFilter
      ? title === requestTypeTitleFilter
      : true

    return !shouldRemoveByProduct && titleMatchesFilter
  })
}
